// Implements: F-UIX-017
/**
 * The `?` sheet of the redesign mockup: every global keyboard shortcut, key and purpose. It lists
 * only shortcuts the application answers today. Each key of a combination is its own cap (mockup).
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../shared/i18n";
import type { TranslationKey } from "../../shared/i18n";
import { Kbd } from "../../shared/ui/atoms/Kbd";
import { Dialog } from "../../shared/ui/organisms/Dialog";
import styles from "./ShortcutsDialog.module.css";

type PlainKey = Extract<TranslationKey, `app.kbd.${string}` | `app.shortcuts.${string}`>;

const ROWS: readonly (readonly [key: PlainKey, purpose: PlainKey])[] = [
  ["app.kbd.ctrlK", "app.shortcuts.palette"],
  ["app.kbd.bracket", "app.shortcuts.sidebar"],
  ["app.kbd.question", "app.shortcuts.help"],
  ["app.kbd.ctrlEnter", "app.shortcuts.send"],
  ["app.kbd.shiftBackslash", "app.shortcuts.focus"],
  ["app.kbd.esc", "app.shortcuts.close"],
];

export interface ShortcutsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function ShortcutsDialog({ open, onClose }: ShortcutsDialogProps): ReactElement | null {
  const { t } = useTranslation();
  return (
    <Dialog open={open} title={t("app.shortcuts.title")} onClose={onClose}>
      <dl className={styles.list}>
        {ROWS.map(([key, purpose]) => (
          <div key={key} className={styles.row}>
            <dt className={styles.purpose}>{t(purpose)}</dt>
            <dd className={styles.keys}>
              {t(key)
                .split(" ")
                .map((cap) => (
                  <Kbd key={cap}>{cap}</Kbd>
                ))}
            </dd>
          </div>
        ))}
      </dl>
    </Dialog>
  );
}
